import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { Form } from "antd";
import { Link } from "react-router-dom";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import AppInput from "../../../components/ui/AppInput";
import AppButton from "../../../components/ui/AppButton";
import PasswordPolicyHint from "../../../components/common/PasswordPolicyHint";
import { loginSchema, loginInitialValues } from "./LoginForm.schema";

/**
 * LoginForm — Phase 2 hardened login UI.
 *
 * onSubmit receives the form values; the parent shell wires the backend call.
 * The form drives the visible error state (attempts-remaining text, lockout
 * countdown, generic failure) entirely from props so the shell can shape the
 * server response without re-rendering this component.
 */
export default function LoginForm({ onSubmit, submitting, errorState, onChange }) {
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      // Send the username as typed (trimmed); the backend matches it
      // case-insensitively, so no client-side lower-casing is needed.
      await onSubmit({
        username: values.username.trim(),
        password: values.password,
      });
    },
  });

  useEffect(() => {
    if (onChange) onChange(formik.values);
  }, [formik.values, onChange]);

  const fillDemo = (username, password) => {
    formik.setValues({ username, password });
  };

  const lockedUntil = errorState?.lockedUntil || null;
  const countdown = useCountdown(lockedUntil);
  const locked = lockedUntil && countdown > 0;
  // BUG-005: do NOT gate on formik.isValid — browser autofill doesn't fire
  // Formik's change events, so a valid autofilled form would stay disabled.
  // Formik still validates on submit and surfaces field errors.
  const disabled = submitting || locked;

  const eyeToggle = (
    <AppButton
      type="text"
      size="small"
      onClick={() => setShowPassword((s) => !s)}
      aria-label={showPassword ? "Hide password" : "Show password"}
      className="!p-0 !min-w-0 !h-auto !bg-transparent text-mahogany hover:text-cocoa"
      label={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    />
  );

  return (
    <Form
      layout="vertical"
      onFinish={formik.handleSubmit}
      requiredMark={false}
      className="mt-8 space-y-4"
    >
      <AppInput
        id="login-username"
        name="username"
        type="text"
        label="Username"
        required
        size="large"
        autoComplete="username"
        placeholder="Enter username"
        value={formik.values.username}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        status={formik.touched.username && formik.errors.username ? "error" : undefined}
        error={formik.touched.username ? formik.errors.username : undefined}
      />

      <AppInput
        id="login-password"
        name="password"
        type={showPassword ? "text" : "password"}
        label="Password"
        labelExtra={<PasswordPolicyHint />}
        required
        size="large"
        autoComplete="current-password"
        placeholder="Enter your password"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        suffix={eyeToggle}
        restrictLeadingSpace={false}
        restrictConsecutiveSpaces={false}
        status={formik.touched.password && formik.errors.password ? "error" : undefined}
        error={formik.touched.password ? formik.errors.password : undefined}
      />

      <div className="text-right -mt-2">
        <Link
          to="/forgot-password"
          className="text-[11px] text-mahogany hover:text-cocoa underline-offset-2 hover:underline"
        >
          Forgot password?
        </Link>
      </div>

      {errorState?.deviceBlocked ? (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {errorState.message ||
              "This machine is not registered for store access. Please contact your store manager to set it up."}
          </span>
        </div>
      ) : null}

      <AppButton
        htmlType="submit"
        type="primary"
        size="large"
        block
        loading={submitting}
        disabled={disabled}
        label={locked ? `Locked (${formatSeconds(countdown)})` : "Sign In"}
      />

      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-xs text-mahogany uppercase tracking-wider mb-2 text-center">
          Quick fill
        </div>
        <AppButton
          type="default"
          block
          onClick={() => fillDemo("superadmin", "Pass@123")}
          label="Super Admin (seeded) — superadmin / Pass@123"
        />
      </div>
    </Form>
  );
}

function useCountdown(lockedUntilIso) {
  const targetRef = useRef(null);
  const [, force] = useState(0);

  useEffect(() => {
    targetRef.current = lockedUntilIso ? new Date(lockedUntilIso).getTime() : null;
    if (!targetRef.current) return undefined;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lockedUntilIso]);

  if (!targetRef.current) return 0;
  return Math.max(0, Math.floor((targetRef.current - Date.now()) / 1000));
}

function formatSeconds(total) {
  if (total <= 0) return "0s";
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}
